import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import {
  ArrowLeft, DollarSign, Receipt, RefreshCw, Upload, Pencil, AlertTriangle,
  FileText, Download, ExternalLink, Building2, User, Phone, Calendar, Eraser, Trash2,
} from "lucide-react";
import Layout from "../components/Layout";
import { ESTADO_PROPIEDAD, formatCurrency, formatDate, formatDateTime } from "../lib/propiedadUtils";
import {
  RegistrarPagoSheet, AgregarGastoDialog, SubirDocumentoDialog, AgregarNotaDialog, RenovarContratoDialog, TerminarContratoDialog,
} from "../components/propiedad/AccionesRapidas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => ({ Authorization: `Bearer ${localStorage.getItem("token")}` });

const cargoEstadoClass = {
  Pendiente: "bg-gray-100 text-gray-700",
  Parcial: "bg-amber-100 text-amber-700",
  Pagado: "bg-emerald-100 text-emerald-700",
  Vencido: "bg-red-100 text-red-700",
  Aplicado: "bg-blue-100 text-blue-700",
};

export default function PropiedadDetail({ user, onLogout }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [prop, setProp] = useState(null);
  const [loading, setLoading] = useState(true);
  const [movimientos, setMovimientos] = useState(null);
  const [contratosData, setContratosData] = useState(null);
  const [documentos, setDocumentos] = useState(null);
  const [historial, setHistorial] = useState(null);
  const [showEdit, setShowEdit] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("resumen");
  const [showPago, setShowPago] = useState(false);
  const [showGasto, setShowGasto] = useState(false);
  const [showDocumento, setShowDocumento] = useState(false);
  const [showNota, setShowNota] = useState(false);
  const [showRenovar, setShowRenovar] = useState(false);
  const [showTerminar, setShowTerminar] = useState(false);
  const [showLimpiar, setShowLimpiar] = useState(false);
  const [limpiando, setLimpiando] = useState(false);
  const [showEliminar, setShowEliminar] = useState(false);
  const [eliminando, setEliminando] = useState(false);
  const [confirmNombre, setConfirmNombre] = useState("");

  useEffect(() => {
    fetchProp();
  }, [id]);

  const fetchProp = async () => {
    try {
      const response = await axios.get(`${API}/propiedades/${id}`, { headers: authHeader() });
      setProp(response.data);
    } catch (error) {
      toast.error("Error al cargar la propiedad");
      navigate("/propiedades");
    } finally {
      setLoading(false);
    }
  };

  const fetchTab = async (tab) => {
    try {
      if (tab === "movimientos" && !movimientos) {
        const r = await axios.get(`${API}/propiedades/${id}/movimientos`, { headers: authHeader() });
        setMovimientos(r.data);
      } else if (tab === "contrato" && !contratosData) {
        const r = await axios.get(`${API}/propiedades/${id}/contratos`, { headers: authHeader() });
        setContratosData(r.data);
      } else if (tab === "documentos" && !documentos) {
        const r = await axios.get(`${API}/propiedades/${id}/documentos`, { headers: authHeader() });
        setDocumentos(r.data);
      } else if (tab === "historial" && !historial) {
        const r = await axios.get(`${API}/propiedades/${id}/historial`, { headers: authHeader() });
        setHistorial(r.data);
      }
    } catch (error) {
      toast.error("Error al cargar la información");
    }
  };

  const refreshAll = async () => {
    setMovimientos(null);
    setContratosData(null);
    setDocumentos(null);
    setHistorial(null);
    await fetchProp();
    if (activeTab !== "resumen") {
      try {
        const path = activeTab === "contrato" ? "contratos" : activeTab;
        const r = await axios.get(`${API}/propiedades/${id}/${path}`, { headers: authHeader() });
        if (activeTab === "movimientos") setMovimientos(r.data);
        else if (activeTab === "contrato") setContratosData(r.data);
        else if (activeTab === "documentos") setDocumentos(r.data);
        else if (activeTab === "historial") setHistorial(r.data);
      } catch (error) {
        // el tab se recarga al cambiar
      }
    }
  };

  const limpiarExpediente = async () => {
    setLimpiando(true);
    try {
      const r = await axios.post(`${API}/propiedades/${id}/limpiar-expediente`, {}, { headers: authHeader() });
      const d = r.data;
      toast.success(`Expediente actualizado: ${d.contratos_eliminados} contratos, ${d.cargos_eliminados} cargos, ${d.pagos_eliminados} pagos y ${d.gastos_eliminados} gastos eliminados`);
      setShowLimpiar(false);
      await refreshAll();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al limpiar el expediente");
    } finally {
      setLimpiando(false);
    }
  };

  const eliminarPropiedad = async () => {
    setEliminando(true);
    try {
      const r = await axios.delete(`${API}/propiedades/${id}`, { headers: authHeader() });
      toast.success(r.data.message);
      navigate("/propiedades");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error al eliminar la propiedad");
      setEliminando(false);
    }
  };

  const handleDownload = async (doc) => {
    if (doc.url) {
      window.open(doc.url, "_blank");
      return;
    }
    try {
      const response = await axios.get(`${BACKEND_URL}${doc.download_url}`, {
        headers: authHeader(),
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", doc.nombre || "documento");
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("No se pudo descargar el documento");
    }
  };

  const openEdit = () => {
    setEditForm({
      nombre_corto: prop.nombre_corto || "",
      direccion: prop.direccion || "",
      comision_porcentaje: prop.comision_porcentaje ?? "",
      estado_manual: prop.estado_manual || "automatico",
      notas: prop.notas || "",
    });
    setShowEdit(true);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API}/propiedades/${id}`,
        {
          nombre_corto: editForm.nombre_corto,
          direccion: editForm.direccion,
          comision_porcentaje: editForm.comision_porcentaje !== "" ? parseFloat(editForm.comision_porcentaje) : null,
          cuota_condominal: editForm.cuota_condominal !== "" ? parseFloat(editForm.cuota_condominal) : null,
          cuota_condominal_paga: editForm.cuota_condominal !== "" ? editForm.cuota_condominal_paga : null,
          estado_manual: editForm.estado_manual === "automatico" ? "" : editForm.estado_manual,
          notas: editForm.notas,
        },
        { headers: authHeader() }
      );
      toast.success("Propiedad actualizada");
      setShowEdit(false);
      setLoading(true);
      await fetchProp();
    } catch (error) {
      toast.error("Error al guardar los cambios");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !prop) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center py-20 text-gray-500">Cargando propiedad...</div>
      </Layout>
    );
  }

  const estadoCfg = ESTADO_PROPIEDAD[prop.estado] || { label: prop.estado, className: "bg-gray-100 text-gray-700" };
  const resumen = prop.resumen || {};
  const esPropietario = user.rol === "propietario";

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="space-y-6" data-testid="propiedad-detail-page">
        {/* Back */}
        <button
          onClick={() => navigate("/propiedades")}
          className="flex items-center gap-2 text-sm text-gray-500 hover:text-gray-900"
          data-testid="volver-propiedades-btn"
        >
          <ArrowLeft className="w-4 h-4" /> Volver a propiedades
        </button>

        {/* Header */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
              <div className="space-y-2">
                <div className="flex flex-wrap items-center gap-3">
                  <h1 className="text-2xl sm:text-3xl font-bold text-gray-900" data-testid="propiedad-nombre">
                    {prop.nombre_corto}
                  </h1>
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${estadoCfg.className}`} data-testid="propiedad-estado-badge">
                    {estadoCfg.label}
                  </span>
                </div>
                <p className="text-sm text-gray-500 flex items-center gap-1.5">
                  <Building2 className="w-4 h-4" /> {prop.direccion} {prop.tipo ? `· ${prop.tipo}` : ""}
                </p>
                <div className="flex flex-wrap gap-x-6 gap-y-1 text-sm text-gray-700 pt-1">
                  <span className="flex items-center gap-1.5">
                    <User className="w-4 h-4 text-gray-400" /> Propietario: <strong>{prop.propietario_nombre || "—"}</strong>
                  </span>
                  {prop.inquilino_nombre && (
                    <span className="flex items-center gap-1.5">
                      <User className="w-4 h-4 text-gray-400" /> Inquilino: <strong>{prop.inquilino_nombre}</strong>
                    </span>
                  )}
                  {prop.inquilino_telefono && (
                    <span className="flex items-center gap-1.5">
                      <Phone className="w-4 h-4 text-gray-400" /> {prop.inquilino_telefono}
                    </span>
                  )}
                </div>
              </div>

              {!esPropietario && (
                <div className="flex flex-wrap gap-2">
                  <Button onClick={() => setShowPago(true)} data-testid="registrar-pago-btn">
                    <DollarSign className="w-4 h-4 mr-1" /> Registrar pago
                  </Button>
                  <Button variant="outline" onClick={() => setShowGasto(true)} disabled={!prop.contrato_vigente_id} data-testid="agregar-gasto-btn">
                    <Receipt className="w-4 h-4 mr-1" /> Agregar gasto
                  </Button>
                  <Button variant="outline" onClick={() => setShowRenovar(true)} disabled={!prop.contrato_vigente_id} data-testid="renovar-contrato-btn">
                    <RefreshCw className="w-4 h-4 mr-1" /> Renovar
                  </Button>
                  <Button variant="outline" onClick={() => setShowDocumento(true)} disabled={!prop.contrato_vigente_id} data-testid="subir-documento-btn">
                    <Upload className="w-4 h-4 mr-1" /> Subir documento
                  </Button>
                  <Button variant="outline" onClick={() => setShowNota(true)} data-testid="agregar-nota-btn">
                    <FileText className="w-4 h-4 mr-1" /> Nota
                  </Button>
                  <Button variant="outline" onClick={openEdit} data-testid="editar-propiedad-btn">
                    <Pencil className="w-4 h-4 mr-1" /> Editar
                  </Button>
                  {user.rol === "maestro" && (
                    <Button variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowLimpiar(true)} data-testid="limpiar-expediente-btn">
                      <Eraser className="w-4 h-4 mr-1" /> Limpiar expediente
                    </Button>
                  )}
                  {user.rol === "maestro" && (
                    <Button variant="outline" className="text-red-700 border-red-300 hover:bg-red-50" onClick={() => { setConfirmNombre(""); setShowEliminar(true); }} data-testid="eliminar-propiedad-btn">
                      <Trash2 className="w-4 h-4 mr-1" /> Eliminar propiedad
                    </Button>
                  )}
                </div>
              )}
            </div>

            {/* Métricas del encabezado */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6 pt-5 border-t border-gray-100">
              <div>
                <p className="text-xs text-gray-500">Renta mensual</p>
                <p className="text-lg font-semibold text-gray-900" data-testid="header-renta">
                  {prop.renta_mensual != null ? formatCurrency(prop.renta_mensual) : "—"}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Saldo pendiente</p>
                <p className={`text-lg font-semibold ${prop.saldo_vencido > 0 ? "text-red-600" : prop.saldo_pendiente > 0 ? "text-amber-600" : "text-emerald-600"}`} data-testid="header-saldo">
                  {formatCurrency(prop.saldo_pendiente || 0)}
                </p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Próximo vencimiento</p>
                <p className="text-lg font-semibold text-gray-900">{formatDate(prop.proximo_vencimiento)}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Contrato termina</p>
                <p className="text-lg font-semibold text-gray-900">
                  {formatDate(prop.fecha_termino_contrato)}
                  {prop.dias_para_vencimiento != null && prop.dias_para_vencimiento >= 0 && prop.dias_para_vencimiento <= 90 && (
                    <span className="text-xs text-amber-600 ml-1">({prop.dias_para_vencimiento} días)</span>
                  )}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Tabs defaultValue="resumen" onValueChange={(t) => { setActiveTab(t); fetchTab(t); }} className="space-y-4">
          <TabsList className="w-full sm:w-auto flex flex-wrap h-auto">
            <TabsTrigger value="resumen" data-testid="tab-resumen">Resumen</TabsTrigger>
            <TabsTrigger value="movimientos" data-testid="tab-movimientos">Movimientos</TabsTrigger>
            <TabsTrigger value="contrato" data-testid="tab-contrato">Contrato</TabsTrigger>
            <TabsTrigger value="documentos" data-testid="tab-documentos">Documentos</TabsTrigger>
            <TabsTrigger value="historial" data-testid="tab-historial">Historial</TabsTrigger>
          </TabsList>

          {/* RESUMEN */}
          <TabsContent value="resumen" className="space-y-4">
            {resumen.alertas && resumen.alertas.length > 0 && (
              <Card data-testid="alertas-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4 text-amber-500" /> Pendientes y alertas
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {resumen.alertas.map((a, i) => (
                    <div
                      key={i}
                      className={`text-sm px-3 py-2 rounded-lg ${
                        a.tipo === "alta" ? "bg-red-50 text-red-700" : a.tipo === "media" ? "bg-amber-50 text-amber-700" : "bg-blue-50 text-blue-700"
                      }`}
                    >
                      {a.mensaje}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Situación de cobranza</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Renta mensual</span><strong>{prop.renta_mensual != null ? formatCurrency(prop.renta_mensual) : "—"}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Saldo pendiente</span><strong className={prop.saldo_pendiente > 0 ? "text-amber-600" : ""}>{formatCurrency(prop.saldo_pendiente || 0)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Saldo vencido</span><strong className={prop.saldo_vencido > 0 ? "text-red-600" : ""}>{formatCurrency(prop.saldo_vencido || 0)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Último pago</span>
                    <strong>
                      {resumen.ultimo_pago ? `${formatCurrency(resumen.ultimo_pago.importe)} · ${formatDate(resumen.ultimo_pago.fecha)}` : "Sin pagos"}
                    </strong>
                  </div>
                  <div className="flex justify-between"><span className="text-gray-500">Próximo vencimiento</span><strong>{formatDate(prop.proximo_vencimiento)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Depósito en garantía</span><strong>{prop.deposito_garantia != null ? formatCurrency(prop.deposito_garantia) : "—"}</strong></div>
                </CardContent>
              </Card>

              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Resumen del mes</CardTitle></CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex justify-between"><span className="text-gray-500">Ingresos del mes</span><strong className="text-emerald-600">{formatCurrency(resumen.ingresos_mes || 0)}</strong></div>
                  <div className="flex justify-between"><span className="text-gray-500">Gastos del mes</span><strong className="text-red-600">{formatCurrency(resumen.gastos_mes || 0)}</strong></div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">Comisión de administración {prop.comision_porcentaje != null ? `(${prop.comision_porcentaje}%)` : ""}</span>
                    <strong>{formatCurrency(resumen.comision_mes || 0)}</strong>
                  </div>
                  <div className="flex justify-between pt-2 border-t border-gray-100">
                    <span className="text-gray-700 font-medium">Neto estimado para el propietario</span>
                    <strong className={resumen.neto_estimado >= 0 ? "text-emerald-700" : "text-red-700"}>{formatCurrency(resumen.neto_estimado || 0)}</strong>
                  </div>
                  {prop.comision_porcentaje == null && (
                    <p className="text-xs text-amber-600 bg-amber-50 rounded-lg px-3 py-2">
                      No hay comisión configurada. Edita la propiedad o el contrato para definir el % de administración.
                    </p>
                  )}
                </CardContent>
              </Card>
            </div>

            {prop.notas && (
              <Card>
                <CardHeader className="pb-2"><CardTitle className="text-base">Notas</CardTitle></CardHeader>
                <CardContent><p className="text-sm text-gray-700 whitespace-pre-wrap">{prop.notas}</p></CardContent>
              </Card>
            )}
          </TabsContent>

          {/* MOVIMIENTOS */}
          <TabsContent value="movimientos">
            <Card>
              <CardContent className="p-0 overflow-x-auto">
                {!movimientos ? (
                  <div className="py-10 text-center text-gray-500">Cargando movimientos...</div>
                ) : movimientos.length === 0 ? (
                  <div className="py-10 text-center text-gray-500">Sin movimientos registrados</div>
                ) : (
                  <table className="w-full text-sm" data-testid="movimientos-tabla">
                    <thead>
                      <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                        <th className="px-4 py-3">Fecha</th>
                        <th className="px-4 py-3">Movimiento</th>
                        <th className="px-4 py-3 text-right">Cargo</th>
                        <th className="px-4 py-3 text-right">Abono</th>
                        <th className="px-4 py-3 text-right">Saldo</th>
                        <th className="px-4 py-3">Estatus</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[...movimientos].reverse().map((m, i) => (
                        <tr key={`${m.ref_id}-${i}`} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap">{formatDate(m.fecha)}</td>
                          <td className="px-4 py-3">
                            <span className="text-gray-900">{m.descripcion}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-gray-900">{m.cargo != null ? formatCurrency(m.cargo) : ""}</td>
                          <td className="px-4 py-3 text-right text-emerald-700">{m.abono != null ? formatCurrency(m.abono) : ""}</td>
                          <td className="px-4 py-3 text-right text-gray-700">{m.saldo != null ? formatCurrency(m.saldo) : ""}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${cargoEstadoClass[m.estatus] || "bg-gray-100 text-gray-700"}`}>
                              {m.estatus}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </CardContent>
            </Card>
          </TabsContent>

          {/* CONTRATO */}
          <TabsContent value="contrato" className="space-y-4">
            {!contratosData ? (
              <Card><CardContent className="py-10 text-center text-gray-500">Cargando contratos...</CardContent></Card>
            ) : contratosData.contratos.length === 0 ? (
              <Card><CardContent className="py-10 text-center text-gray-500">Esta propiedad no tiene contratos registrados</CardContent></Card>
            ) : (
              <>
                {contratosData.contratos
                  .filter((c) => c.id === contratosData.vigente_id)
                  .map((c) => (
                    <Card key={c.id} data-testid="contrato-vigente-card">
                      <CardHeader className="pb-2">
                        <div className="flex items-center justify-between flex-wrap gap-2">
                          <CardTitle className="text-base">
                            Contrato vigente · {c.numero_contrato}
                            <span className="ml-2 inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700">{c.estatus}</span>
                          </CardTitle>
                          {!esPropietario && (
                            <div className="flex gap-2">
                              <Button size="sm" variant="outline" onClick={() => navigate(`/contratos/${c.id}`)} data-testid="ver-contrato-btn">Ver detalle</Button>
                              <Button size="sm" variant="outline" onClick={() => navigate(`/contratos/editar/${c.id}`)}>Editar</Button>
                              <Button size="sm" variant="outline" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setShowTerminar(true)} data-testid="terminar-contrato-btn">
                                Terminar / Desocupar
                              </Button>
                            </div>
                          )}
                        </div>
                      </CardHeader>
                      <CardContent>
                        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                          <div><p className="text-gray-500 text-xs">Inquilino</p><p className="font-medium">{c.inquilino_nombre}</p></div>
                          <div><p className="text-gray-500 text-xs">Inicio</p><p className="font-medium">{formatDate(c.fecha_inicio)}</p></div>
                          <div><p className="text-gray-500 text-xs">Término</p><p className="font-medium">{formatDate(c.fecha_termino)}</p></div>
                          <div><p className="text-gray-500 text-xs">Renta</p><p className="font-medium">{formatCurrency(c.renta_mensual)}</p></div>
                          <div><p className="text-gray-500 text-xs">Día de pago</p><p className="font-medium">{c.dia_pago}</p></div>
                          <div><p className="text-gray-500 text-xs">Depósito</p><p className="font-medium">{formatCurrency(c.deposito_garantia)}</p></div>
                          <div><p className="text-gray-500 text-xs">Incremento anual</p><p className="font-medium">{c.incremento_anual ? `Sí (${c.incremento_porcentaje ?? "—"}%)` : "No"}</p></div>
                          <div><p className="text-gray-500 text-xs">Comisión admón.</p><p className="font-medium">{c.comision_porcentaje != null ? `${c.comision_porcentaje}%` : "No definida"}</p></div>
                          <div><p className="text-gray-500 text-xs">Fiadores / avales</p><p className="font-medium">{(c.fiadores || []).length > 0 ? c.fiadores.map((f) => f.nombre_completo).join(", ") : "Sin fiadores"}</p></div>
                        </div>
                      </CardContent>
                    </Card>
                  ))}

                {contratosData.contratos.filter((c) => c.id !== contratosData.vigente_id).length > 0 && (
                  <Card>
                    <CardHeader className="pb-2"><CardTitle className="text-base">Historial de contratos</CardTitle></CardHeader>
                    <CardContent className="p-0 overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-200 text-left text-xs uppercase text-gray-500">
                            <th className="px-4 py-2">Folio</th>
                            <th className="px-4 py-2">Inquilino</th>
                            <th className="px-4 py-2">Periodo</th>
                            <th className="px-4 py-2 text-right">Renta</th>
                            <th className="px-4 py-2">Estatus</th>
                            <th className="px-4 py-2"></th>
                          </tr>
                        </thead>
                        <tbody>
                          {contratosData.contratos.filter((c) => c.id !== contratosData.vigente_id).map((c) => (
                            <tr key={c.id} className="border-b border-gray-100 hover:bg-gray-50">
                              <td className="px-4 py-2 font-medium">{c.numero_contrato}</td>
                              <td className="px-4 py-2">{c.inquilino_nombre}</td>
                              <td className="px-4 py-2">{formatDate(c.fecha_inicio)} → {formatDate(c.fecha_termino)}</td>
                              <td className="px-4 py-2 text-right">{formatCurrency(c.renta_mensual)}</td>
                              <td className="px-4 py-2"><span className="inline-flex px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">{c.estatus}</span></td>
                              <td className="px-4 py-2 text-right">
                                <Button size="sm" variant="ghost" onClick={() => navigate(`/contratos/${c.id}`)}>Ver</Button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </CardContent>
                  </Card>
                )}
              </>
            )}
          </TabsContent>

          {/* DOCUMENTOS */}
          <TabsContent value="documentos">
            {!documentos ? (
              <Card><CardContent className="py-10 text-center text-gray-500">Cargando documentos...</CardContent></Card>
            ) : documentos.length === 0 ? (
              <Card>
                <CardContent className="py-10 text-center text-gray-500">
                  <FileText className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                  No hay documentos asociados a esta propiedad
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="documentos-lista">
                {[...new Set(documentos.map((d) => d.categoria))].map((cat) => (
                  <Card key={cat}>
                    <CardHeader className="pb-2"><CardTitle className="text-base">{cat}</CardTitle></CardHeader>
                    <CardContent className="space-y-1">
                      {documentos.filter((d) => d.categoria === cat).map((d, i) => (
                        <div key={`${cat}-${d.id}-${i}`} className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-gray-50">
                          <div className="min-w-0">
                            <p className="text-sm font-medium text-gray-900 truncate">{d.nombre}</p>
                            <p className="text-xs text-gray-500 truncate">{d.origen} {d.fecha ? `· ${formatDate(d.fecha)}` : ""}</p>
                          </div>
                          <Button size="sm" variant="ghost" onClick={() => handleDownload(d)} data-testid={`descargar-doc-${d.id}`}>
                            {d.url ? <ExternalLink className="w-4 h-4" /> : <Download className="w-4 h-4" />}
                          </Button>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>

          {/* HISTORIAL */}
          <TabsContent value="historial">
            <Card>
              <CardContent className="pt-6">
                {!historial ? (
                  <div className="py-6 text-center text-gray-500">Cargando historial...</div>
                ) : historial.length === 0 ? (
                  <div className="py-6 text-center text-gray-500">Sin actividad registrada</div>
                ) : (
                  <div className="space-y-4" data-testid="historial-lista">
                    {historial.map((e, i) => (
                      <div key={i} className="flex gap-3">
                        <div className={`mt-1.5 w-2 h-2 rounded-full shrink-0 ${
                          e.tipo === "pago" ? "bg-emerald-500" : e.tipo === "gasto" ? "bg-red-400" : e.tipo === "renovacion" ? "bg-indigo-400" : e.tipo === "documento" ? "bg-blue-400" : "bg-gray-400"
                        }`} />
                        <div className="min-w-0">
                          <p className="text-sm text-gray-900">{e.descripcion}</p>
                          <p className="text-xs text-gray-500">
                            {formatDateTime(e.fecha)}
                            {e.usuario_nombre ? ` · ${e.usuario_nombre}` : ""}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Acciones rápidas (Fase 3) */}
      <RegistrarPagoSheet open={showPago} onOpenChange={setShowPago} propiedad={prop} onSuccess={refreshAll} />
      <AgregarGastoDialog open={showGasto} onOpenChange={setShowGasto} propiedad={prop} onSuccess={refreshAll} />
      <SubirDocumentoDialog open={showDocumento} onOpenChange={setShowDocumento} propiedad={prop} onSuccess={refreshAll} />
      <AgregarNotaDialog open={showNota} onOpenChange={setShowNota} propiedad={prop} onSuccess={refreshAll} />
      <RenovarContratoDialog open={showRenovar} onOpenChange={setShowRenovar} propiedad={prop} onSuccess={refreshAll} />
      <TerminarContratoDialog
        open={showTerminar}
        onOpenChange={setShowTerminar}
        contratoId={prop.contrato_vigente_id}
        contratoNumero={prop.numero_contrato}
        onSuccess={refreshAll}
      />

      {/* Modal editar propiedad */}
      <Dialog open={showEdit} onOpenChange={setShowEdit}>
        <DialogContent data-testid="editar-propiedad-modal">
          <DialogHeader>
            <DialogTitle>Editar propiedad</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Nombre corto</Label>
              <Input
                value={editForm.nombre_corto || ""}
                onChange={(e) => setEditForm({ ...editForm, nombre_corto: e.target.value })}
                data-testid="edit-nombre-input"
              />
            </div>
            <div className="space-y-2">
              <Label>Dirección</Label>
              <Input
                value={editForm.direccion || ""}
                onChange={(e) => setEditForm({ ...editForm, direccion: e.target.value })}
                data-testid="edit-direccion-input"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Comisión admón. (%)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  max="100"
                  value={editForm.comision_porcentaje ?? ""}
                  onChange={(e) => setEditForm({ ...editForm, comision_porcentaje: e.target.value })}
                  data-testid="edit-comision-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Estado manual</Label>
                <Select
                  value={editForm.estado_manual || "automatico"}
                  onValueChange={(v) => setEditForm({ ...editForm, estado_manual: v })}
                >
                  <SelectTrigger data-testid="edit-estado-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="automatico">Automático (según contrato)</SelectItem>
                    <SelectItem value="en_mantenimiento">En mantenimiento</SelectItem>
                    <SelectItem value="uso_propietario">Uso del propietario</SelectItem>
                    <SelectItem value="inactiva">Inactiva</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>Cuota condominal ($/mes)</Label>
                <Input
                  type="number"
                  step="0.01"
                  min="0"
                  value={editForm.cuota_condominal ?? ""}
                  onChange={(e) => setEditForm({ ...editForm, cuota_condominal: e.target.value })}
                  data-testid="edit-cuota-input"
                />
              </div>
              <div className="space-y-2">
                <Label>¿Quién la paga?</Label>
                <Select
                  value={editForm.cuota_condominal_paga || "inquilino"}
                  onValueChange={(v) => setEditForm({ ...editForm, cuota_condominal_paga: v })}
                >
                  <SelectTrigger data-testid="edit-cuota-paga-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="inquilino">El inquilino</SelectItem>
                    <SelectItem value="propietario">El propietario directo</SelectItem>
                    <SelectItem value="administracion">La administración (se descuenta)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-2">
              <Label>Notas internas</Label>
              <Textarea
                rows={3}
                value={editForm.notas || ""}
                onChange={(e) => setEditForm({ ...editForm, notas: e.target.value })}
                data-testid="edit-notas-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEdit(false)}>Cancelar</Button>
            <Button onClick={handleSaveEdit} disabled={saving} data-testid="guardar-propiedad-btn">
              {saving ? "Guardando..." : "Guardar cambios"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Limpiar expediente */}
      <Dialog open={showLimpiar} onOpenChange={setShowLimpiar}>
        <DialogContent data-testid="limpiar-expediente-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <AlertTriangle className="w-5 h-5" /> Limpiar expediente de {prop?.nombre_corto}
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2 text-sm text-gray-600">
                <p>Esta acción <span className="font-semibold text-red-600">elimina de forma definitiva</span> todo el historial de la propiedad:</p>
                <ul className="list-disc pl-5 space-y-1">
                  <li>Contratos anteriores (vencidos, terminados, renovados o archivados) con sus cargos y pagos</li>
                  <li>Gastos con fecha fuera del rango del contrato vigente</li>
                </ul>
                <p>Se conserva únicamente:</p>
                <ul className="list-disc pl-5 space-y-1 text-emerald-700">
                  <li>El contrato vigente completo (cargos, pagos y archivos)</li>
                  <li>Los gastos dentro de las fechas del contrato vigente</li>
                  <li>Los documentos de la propiedad</li>
                </ul>
                {!prop?.contrato_vigente_id && (
                  <p className="text-red-600 font-medium">Esta propiedad NO tiene contrato vigente: se borrará TODO el historial y quedará limpia.</p>
                )}
                <p className="font-medium text-gray-800">No hay respaldo ni forma de deshacer esta operación.</p>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowLimpiar(false)}>Cancelar</Button>
            <Button onClick={limpiarExpediente} disabled={limpiando} className="bg-red-600 hover:bg-red-700" data-testid="confirmar-limpiar-btn">
              {limpiando ? "Limpiando..." : "Sí, limpiar expediente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Eliminar propiedad definitivamente */}
      <Dialog open={showEliminar} onOpenChange={setShowEliminar}>
        <DialogContent data-testid="eliminar-propiedad-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-700">
              <Trash2 className="w-5 h-5" /> Eliminar propiedad definitivamente
            </DialogTitle>
            <DialogDescription asChild>
              <div className="space-y-3 pt-2 text-sm text-gray-600">
                <p>Se borrará <span className="font-semibold text-red-600">TODO de forma permanente</span>: la propiedad <span className="font-semibold">{prop?.nombre_corto}</span>, todos sus contratos (incluido el vigente), cargos, pagos, gastos, documentos, notas e historial.</p>
                <p className="font-medium text-gray-800">No hay respaldo ni forma de deshacer esta operación.</p>
                <div className="space-y-1.5">
                  <Label>Para confirmar, escribe el nombre de la propiedad: <span className="font-semibold">{prop?.nombre_corto}</span></Label>
                  <Input value={confirmNombre} onChange={(e) => setConfirmNombre(e.target.value)} placeholder={prop?.nombre_corto} data-testid="confirmar-nombre-input" />
                </div>
              </div>
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEliminar(false)}>Cancelar</Button>
            <Button onClick={eliminarPropiedad} disabled={eliminando || confirmNombre.trim() !== (prop?.nombre_corto || "").trim()}
              className="bg-red-600 hover:bg-red-700" data-testid="confirmar-eliminar-btn">
              {eliminando ? "Eliminando..." : "Eliminar definitivamente"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
